import React from 'react';
import { MainLayout } from 'layouts/MainLayout';
import { Box, styled } from '@mui/material';
import { MARKETING_3RD_PARTY_PERKS, MARKETING_PEPPER_PERKS } from 'config/routes';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { PepperPerksView } from './PepperPerksView';
import { LinkTab } from 'ui/LinkTab';
import { useParams } from 'react-router-dom';
import { ExternalPerksView } from './ExternalPerksView';

const StyledTabPanel = styled(TabPanel)<{ hasExternalPerk?: boolean }>(
    ({ hasExternalPerk = true, theme }) => ({
        padding: 0,
        marginTop: hasExternalPerk ? theme.spacing(3) : 0,
        height: '100%'
    })
);

const StyledTab = styled(TabList)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main
}));

const StyledTabLink = styled(LinkTab)(({ theme }) => ({
    color: theme.palette.common.white,
    '&.Mui-selected': {
        color: theme.palette.common.white
    }
}));

const PerksPage = () => {
    const { type } = useParams<{ type: 'pepper' | 'external' | undefined }>();
    const [globalSettings, setGlobalSettings] = React.useState<IPublicAndPrivateSettings>({});

    const loadGlobalSettings = React.useCallback(async () => {
        const settings = await new LocationSettingsApi().getPublicAndPrivateSettings(null);
        setGlobalSettings(settings);
    }, []);

    const globalLoyaltyProvider = React.useMemo(() => globalSettings?.loyaltyProvider, [globalSettings]);
    React.useEffect(() => {
        loadGlobalSettings();
    }, [loadGlobalSettings]);

    const [value, setValue] = React.useState(() => {
        if (type === 'external') {
            return MARKETING_3RD_PARTY_PERKS;
        }

        return MARKETING_PEPPER_PERKS;
    });

    const handleChange = React.useCallback((_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    }, []);
    const hasExternalPerkSetting = React.useMemo(
        () => !!globalLoyaltyProvider && globalLoyaltyProvider !== 'PEPPER',
        [globalLoyaltyProvider]
    );

    return (
        <MainLayout pageName="Perks" pageDescription="Incentivize your customers with loyalty schemes.">
            <Box paddingBottom={16} height="100%">
                <TabContext value={value}>
                    {hasExternalPerkSetting && (
                        <StyledTab
                            variant="fullWidth"
                            value={value}
                            indicatorColor="primary"
                            onChange={handleChange}
                            aria-label="nav tabs example"
                        >
                            {hasExternalPerkSetting && (
                                <StyledTabLink
                                    value={MARKETING_3RD_PARTY_PERKS}
                                    label={`${globalLoyaltyProvider}`}
                                    href={MARKETING_3RD_PARTY_PERKS}
                                />
                            )}
                            <StyledTabLink
                                value={MARKETING_PEPPER_PERKS}
                                label="Pepper"
                                href={MARKETING_PEPPER_PERKS}
                            />
                        </StyledTab>
                    )}
                    <StyledTabPanel hasExternalPerk={hasExternalPerkSetting} value={MARKETING_PEPPER_PERKS}>
                        <PepperPerksView />
                    </StyledTabPanel>
                    {!!globalLoyaltyProvider && globalLoyaltyProvider !== 'PEPPER' && (
                        <StyledTabPanel value={MARKETING_3RD_PARTY_PERKS}>
                            <ExternalPerksView loyaltyProvider={globalLoyaltyProvider} />
                        </StyledTabPanel>
                    )}
                </TabContext>
            </Box>
        </MainLayout>
    );
};

export default PerksPage;
